import { useCallback, useEffect, useRef, useState } from "react";
import type { ModuleId, ModuleRecord } from "../types";
import { applyIframeSafetyLayer, applyLitePresentation, type CollapseConfig } from "../utils/iframeSafety";

// Ícone de gabarito (checkmark list)
function GabaritoIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  );
}

// Configuracao por modulo de colapso inicial das sections.
// Mods 1-12 (clinicos): todas as secoes comecam abertas.
// Mod 6 (Disturbios): skip — ja usa <details> nativo para cada distúrbio.
// Mod 14 (Calculadoras adulto): skip — HTML tem chip layer próprio (seções → modal, #paciente fixo).
// Mod 15 (Emergências pediátricas): skip — UX própria (chips/modais + seções).
// Mods 16/17 (Caderno/Refs): skip — usam <details> nativo.
/* Cada modulo abre como INDICE: a primeira secao aberta, as demais recolhidas.
   `keepExpandedIds` nao-vazio e o que faz as outras iniciarem fechadas — com `{}`,
   como estava, TODAS abriam.

   Medido a 375px com tudo aberto: Mod. 10 com 58 telas de rolagem, Mod. 7 com 57,
   Mod. 4 com 55, Mod. 1 com 51. Ninguem percorre isso a beira do leito — e, pior,
   ninguem descobre o que ha no modulo sem percorrer. Recolhido, o indice inteiro cabe
   em uma tela e a reducao fica entre 71% e 93%.

   O id e o da PRIMEIRA secao de cada modulo; renomear essa secao no HTML sem mexer
   aqui faz o modulo abrir inteiro recolhido — nao quebra, mas perde a de entrada.

   Sem modo acordeao de proposito: quem compara duas secoes precisa das duas abertas.
   Link direto para uma secao recolhida continua abrindo-a sozinho (interceptInternalLinks). */
export const COLLAPSE_BY_MODULE: Partial<Record<ModuleId, CollapseConfig | undefined>> = {
  "modulo-01": { keepExpandedIds: ["intro"] },
  /* Abre em "Primeiros 60 segundos apos a passagem do tubo", nao em "Objetivo do
     modulo": meta-texto gasta a tela de entrada de um manual de beira-leito. */
  "modulo-02": { keepExpandedIds: ["primeiros-segundos"] },
  "modulo-03": { keepExpandedIds: ["intro"] },
  "modulo-04": { keepExpandedIds: ["intro"] },
  "modulo-05": { keepExpandedIds: ["intro"] },
  "modulo-06": { keepExpandedIds: ["intro"], skipSectionsWithDetails: true },
  "modulo-07": { keepExpandedIds: ["sepse"] },
  "modulo-08": { keepExpandedIds: ["definicao"] },
  "modulo-09": { keepExpandedIds: ["definicao"] },
  /* Indice puro: Neuroemergencias reune AVCi, AIT, HIC, HSA e status epileptico —
     assuntos independentes. Abrir num deles sugere que o modulo e sobre ele, e era a
     secao mais longa: sozinha respondia por 13 das telas de rolagem. */
  "modulo-10": { startAllCollapsed: true },
  "modulo-11": { keepExpandedIds: ["sec1"] },
  "modulo-12": { keepExpandedIds: ["categorias"] },
  "modulo-13": { keepExpandedIds: ["criterios"] },
  /* Abre na pergunta que o modulo responde — a frase do Mod. 5 que ele continua —,
     nao numa lista de marcadores. As onze secoes seguem a um toque. */
  "modulo-14": { keepExpandedIds: ["intro"] },
  /* Abre na secao que diz o que o modulo NAO repete — a indicacao clinica fica no
     modulo da doenca. Os sete procedimentos seguem a um toque. */
  "modulo-15": { keepExpandedIds: ["intro"] },
  calculadoras: undefined,
  pediatria: undefined,
  caderno: undefined,
  referencias: undefined
};

// Modulos clinicos densos em texto que se beneficiam de compactacao tipografica.
// A cauda (calculadoras, pediatria, caderno, referencias) nao entra: e ferramenta e
// referencia, nao leitura corrida.
export const COMPACT_READING_MODULES = new Set<ModuleId>([
  "modulo-01",
  "modulo-02",
  "modulo-03",
  "modulo-04",
  "modulo-05",
  "modulo-06",
  "modulo-07",
  "modulo-08",
  "modulo-09",
  "modulo-10",
  "modulo-11",
  "modulo-12",
  "modulo-13",
  "modulo-14",
  "modulo-15"
]);

interface ModuleViewerProps {
  module: ModuleRecord;
  targetSectionId?: string;
  modules?: ModuleRecord[];
  onNavigateModule?: (moduleId: ModuleId) => void;
  /** Modo LITE: abre direto o chip/modal indicado assim que o iframe estiver
   *  pronto. E o modulo que calcula — o LITE so pede a abertura. */
  openCalcId?: string;
  /** Modo LITE: esconde a barra de titulo do modulo (o LITE tem a sua). */
  lite?: boolean;
}

function scrollToSection(document: Document, sectionId?: string): void {
  if (!sectionId) {
    document.defaultView?.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  const target = document.getElementById(sectionId);
  if (target) {
    // Se a section esta recolhida (acordeao), abre antes de rolar para que o conteudo
    // fique visivel. Tambem fecha as irmas via click sintetico no h2 (preserva o
    // comportamento de acordeao definido em iframeSafety).
    if (target.classList.contains("codex-section-collapsed")) {
      const h2 = target.querySelector<HTMLElement>(":scope > h2.codex-section-toggle");
      if (h2) h2.click();
    }
    target.scrollIntoView({ block: "start", behavior: "smooth" });
  }
}

export function ModuleViewer({ module, targetSectionId, modules, onNavigateModule, openCalcId, lite }: ModuleViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(0);
  const [fontsReady, setFontsReady] = useState<boolean>(false);
  const [isGabaritoOpen, setIsGabaritoOpen] = useState(false);

  // HTML lazy: módulos 10-12 têm html="" no bundle inicial e carregam sob demanda.
  const [resolvedHtml, setResolvedHtml] = useState<string>(module.html);
  const [htmlLoading, setHtmlLoading] = useState<boolean>(!module.html && !!module.loadHtml);
  const [htmlError, setHtmlError] = useState<boolean>(false);

  useEffect(() => {
    if (module.html) {
      // Módulo eager: HTML já disponível no bundle
      setResolvedHtml(module.html);
      setHtmlLoading(false);
      setHtmlError(false);
    } else if (module.loadHtml) {
      // Módulo lazy: carrega o chunk dinâmico via import.meta.glob
      setResolvedHtml("");
      setHtmlLoading(true);
      setHtmlError(false);
      module.loadHtml()
        .then((html) => {
          setResolvedHtml(html);
          setHtmlLoading(false);
        })
        .catch(() => {
          // Falha no carregamento — mostra mensagem em vez de tela branca
          setHtmlLoading(false);
          setHtmlError(true);
        });
    }
  }, [module.id, module.html, module.loadHtml]);

  // Recebe mensagens do iframe:
  // - codex-iframe-height: atualiza altura (ResizeObserver + RAF, 1×/frame)
  // - codex-fonts-ready: fontes carregadas → módulo pode ser exibido com segurança
  // - codex-gabarito-state: sincroniza botão gabarito externo
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "codex-iframe-height" && typeof event.data.height === "number") {
        const incoming = event.data.height as number;
        setIframeHeight((prev) => (Math.abs(incoming - prev) > 10 ? incoming : prev));
      }
      if (event.data?.type === "codex-fonts-ready") {
        setFontsReady(true);
      }
      if (event.data?.type === "codex-gabarito-state") {
        setIsGabaritoOpen(!!event.data.open);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Ao trocar de módulo: resetar altura, fontes e gabarito
  useEffect(() => {
    setIframeHeight(0);
    setFontsReady(false); // Aguardar codex-fonts-ready do novo módulo
    setIsGabaritoOpen(false);
  }, [module.id]);

  // LITE: pede ao modulo que abra o chip/modal. So depois de fontsReady —
  // antes disso o script do iframe ainda pode nao ter registrado o listener.
  useEffect(() => {
    if (!openCalcId || !fontsReady) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: "codex-open-calc", id: openCalcId },
      "*"
    );
  }, [openCalcId, fontsReady, module.id]);

  // LITE: apresentacao enxuta do resultado (mock aprovado) — cenario e cuidados
  // recolhidos num <details>, nunca removidos. Desliga ao sair do LITE.
  useEffect(() => {
    if (!lite || !fontsReady) return;
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    return applyLitePresentation(doc);
  }, [lite, fontsReady, module.id]);

  const toggleGabarito = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ type: "codex-gabarito-toggle" }, "*");
  }, []);

  const syncIframe = useCallback(() => {
    const document = iframeRef.current?.contentDocument;
    if (!document || !document.head || !document.body) {
      // iframe ainda nao terminou de carregar o srcDoc; onLoad chama de novo
      return;
    }

    let pager;
    if (modules && modules.length > 0 && onNavigateModule) {
      const idx = modules.findIndex((m) => m.id === module.id);
      const prev = idx > 0 ? modules[idx - 1] : undefined;
      const next = idx < modules.length - 1 ? modules[idx + 1] : undefined;
      pager = {
        prev: prev && { id: prev.id, number: prev.number, title: prev.title },
        next: next && { id: next.id, number: next.number, title: next.title },
        onNavigate: onNavigateModule
      };
    }

    const collapse = COLLAPSE_BY_MODULE[module.id];
    const compactReading = COMPACT_READING_MODULES.has(module.id);

    applyIframeSafetyLayer(document, {
      ...(pager ? { pager } : {}),
      ...(collapse ? { collapse } : {}),
      ...(compactReading ? { compactReading: true } : {})
    });
    scrollToSection(document, targetSectionId);
  }, [module.id, modules, onNavigateModule, targetSectionId]);

  useEffect(() => {
    syncIframe();
  }, [module.id, syncIframe]);

  return (
    <section className={`module-viewer${lite ? " module-viewer--lite" : ""}`} aria-label={module.number === null ? module.title : `Módulo ${module.number}: ${module.title}`}>
      {!lite && (
        <div className="module-viewer__toolbar">
          <h2>
            {module.number === null ? module.title : `Módulo ${module.number} — ${module.title}`}
          </h2>
        </div>
      )}

      {/* Botão Gabarito — só aparece no Caderno de questões (modulo-16) */}
      {module.id === "caderno" && (
        <button
          type="button"
          className={`gabarito-trigger${isGabaritoOpen ? " gabarito-trigger--open" : ""}`}
          onClick={toggleGabarito}
          aria-label={isGabaritoOpen ? "Fechar painel de gabarito" : "Abrir painel de gabarito"}
          title="Gabarito"
        >
          <GabaritoIcon />
        </button>
      )}

      <div className="module-viewer__frame-wrap">
        {/* Spinner: aparece enquanto o HTML não carregou (lazy) OU
            enquanto altura/fontes ainda não estão prontas.
            Módulo só fica visível após document.fonts.ready no iframe
            (sinal codex-fonts-ready) — garante que DM Sans já carregou e
            a altura reportada é a final, sem reflow posterior que causaria
            salto de scroll. */}
        {(htmlLoading || (!htmlError && resolvedHtml && (!iframeHeight || !fontsReady))) && (
          <div className="module-viewer__loading" aria-label="Carregando módulo">
            <div className="module-viewer__loading-spinner" />
            <p className="module-viewer__loading-label">Carregando…</p>
          </div>
        )}

        {htmlError && (
          <div className="module-viewer__loading" aria-label="Erro ao carregar">
            <p className="module-viewer__loading-label" style={{ textAlign: "center" }}>
              Não foi possível carregar o módulo.<br />
              <button
                type="button"
                onClick={() => {
                  setHtmlError(false);
                  setHtmlLoading(true);
                  module.loadHtml?.()
                    .then((html) => { setResolvedHtml(html); setHtmlLoading(false); })
                    .catch(() => { setHtmlLoading(false); setHtmlError(true); });
                }}
                style={{ marginTop: 12, padding: "8px 20px", background: "rgba(18,190,209,.2)", border: "1px solid rgba(18,190,209,.4)", borderRadius: 8, color: "#30f1e6", cursor: "pointer", font: "inherit" }}
              >
                Tentar novamente
              </button>
            </p>
          </div>
        )}

        {/* Iframe: renderiza em background assim que HTML está disponível.
            Permanece invisível (opacity:0) enquanto iframeHeight===0 para que
            o ResizeObserver calcule a altura real sem expor o layout ao usuário.
            Quando iframeHeight>0, torna-se visível já no tamanho correto —
            sem nenhuma alteração de layout posterior que causaria salto de scroll. */}
        {!htmlLoading && !htmlError && resolvedHtml && (
          <iframe
            key={module.id}
            ref={iframeRef}
            className="module-viewer__frame"
            srcDoc={resolvedHtml}
            title={`Conteudo do modulo ${module.number}: ${module.title}`}
            sandbox="allow-forms allow-modals allow-popups allow-same-origin allow-scripts"
            onLoad={syncIframe}
            style={
              iframeHeight > 0 && fontsReady
                ? { height: `${iframeHeight}px` }
                // Oculto em background enquanto fontes / altura não estão prontas.
                // Presente no DOM para onLoad + ResizeObserver + document.fonts.ready.
                : { position: "absolute", top: 0, left: 0, width: "100%", opacity: 0, pointerEvents: "none" }
            }
          />
        )}
      </div>
    </section>
  );
}
